<template>
  <transition name="side-modal-fade">
    <div
      v-if="value"
      class="side-modal__backdrop"
      role="presentation"
      @click.self="handleBackdrop"
    >
      <aside
        class="side-modal"
        :style="{ width: resolvedWidth }"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <header class="side-modal__header">
          <slot name="header">
            <div>
              <small v-if="eyebrow">{{ eyebrow }}</small>
              <h2>{{ title }}</h2>
              <p v-if="description">{{ description }}</p>
            </div>
          </slot>
          <button
            class="side-modal__close"
            type="button"
            :aria-label="closeLabel"
            @click="close"
          >
            <slot name="close">×</slot>
          </button>
        </header>

        <div class="side-modal__body">
          <slot />
        </div>

        <footer v-if="hasFooter" class="side-modal__footer">
          <slot name="footer">
            <span>{{ footerMeta }}</span>
            <div class="side-modal__actions">
              <slot name="actions">
                <button
                  v-if="secondaryLabel"
                  type="button"
                  class="side-modal__button side-modal__button--secondary"
                  @click="$emit('secondary')"
                >
                  {{ secondaryLabel }}
                </button>
                <button
                  v-if="primaryLabel"
                  type="button"
                  class="side-modal__button side-modal__button--primary"
                  @click="$emit('primary')"
                >
                  {{ primaryLabel }}
                </button>
              </slot>
            </div>
          </slot>
        </footer>
      </aside>
    </div>
  </transition>
</template>

<script>
export default {
  name: "SideModal",
  props: {
    value: Boolean,
    eyebrow: { type: String, default: "" },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    footerMeta: { type: String, default: "" },
    primaryLabel: { type: String, default: "" },
    secondaryLabel: { type: String, default: "" },
    width: { type: [String, Number], default: "620px" },
    closeLabel: { type: String, default: "Close" },
    closeOnBackdrop: { type: Boolean, default: true },
    closeOnEscape: { type: Boolean, default: true },
    hideFooter: Boolean,
  },
  computed: {
    resolvedWidth() {
      return typeof this.width === "number" ? `${this.width}px` : `min(${this.width}, 94vw)`;
    },
    hasFooter() {
      return !this.hideFooter && Boolean(
        this.footerMeta ||
        this.primaryLabel ||
        this.secondaryLabel ||
        this.$slots.footer ||
        this.$slots.actions
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (typeof document === "undefined") return;
        document.body.style.overflow = open ? "hidden" : "";
      },
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.body.style.overflow = "";
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
    handleBackdrop() {
      if (this.closeOnBackdrop) this.close();
    },
    handleKeydown(event) {
      if (this.value && this.closeOnEscape && event.key === "Escape") this.close();
    },
  },
};
</script>

<style scoped>
.side-modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(11, 13, 12, 0.75);
  backdrop-filter: blur(6px);
}

.side-modal {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  max-width: 94vw;
  display: flex;
  flex-direction: column;
  color: #f3efe6;
  background: #0b0d0c;
  border-left: 1px solid #2a2f2c;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.5);
}

.side-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  padding: 25px 30px;
  background: #131615;
  border-bottom: 1px solid #2a2f2c;
}

.side-modal__header small {
  color: #ff4d1f;
  font: 9px "JetBrains Mono", monospace;
  letter-spacing: 0.2em;
}

.side-modal__header h2 {
  margin: 6px 0;
  font: 600 27px Fraunces, serif;
}

.side-modal__header p {
  margin: 0;
  color: #c9c3b3;
  font-size: 12px;
}

.side-modal__close {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  color: #c9c3b3;
  background: transparent;
  border: 1px solid #2a2f2c;
  font-size: 22px;
  cursor: pointer;
}

.side-modal__close:hover {
  color: #ff4d1f;
  border-color: #ff4d1f;
}

.side-modal__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 25px 30px;
}

.side-modal__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px 30px;
  background: #131615;
  border-top: 1px solid #2a2f2c;
}

.side-modal__footer > span {
  color: #8a857a;
  font: 9px "JetBrains Mono", monospace;
}

.side-modal__actions {
  display: flex;
  gap: 10px;
}

.side-modal__button {
  padding: 10px 16px;
  color: #f3efe6;
  background: transparent;
  border: 1px solid #f3efe6;
  font: 600 11px "Inter Tight", sans-serif;
  letter-spacing: 0.1em;
  cursor: pointer;
}

.side-modal__button--primary {
  color: #0b0d0c;
  background: #ff4d1f;
  border-color: #ff4d1f;
}

.side-modal__button--secondary {
  color: #c9c3b3;
  border-color: #2a2f2c;
}

.side-modal-fade-enter-active,
.side-modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.side-modal-fade-enter-active .side-modal,
.side-modal-fade-leave-active .side-modal {
  transition: transform 0.3s ease;
}

.side-modal-fade-enter,
.side-modal-fade-leave-to {
  opacity: 0;
}

.side-modal-fade-enter .side-modal,
.side-modal-fade-leave-to .side-modal {
  transform: translateX(100%);
}

@media (max-width: 600px) {
  .side-modal__header,
  .side-modal__body,
  .side-modal__footer {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
